import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";

import SupportTicketModal from "../Support/SupportTicketModal";
import {
  getHelpArticleUrl,
  getHelpCenterBaseUrl,
  HELP_ARTICLE_WHATSAPP_EXTENSION,
} from "../../helpers/helpCenter";
import { PLAY_STORE_URL } from "../../helpers/playStore";
import {
  dispatchSupportSummaryRefresh,
  SUPPORT_SUMMARY_REFRESH_EVENT,
} from "../../helpers/supportTicketUx";
import { getAuthRoleNames } from "../../services/authService";
import { getSupportTicketSummary } from "../../services/supportTicketService";
import { ADMIN_ROLES, STUDIO_WEB_ROLES } from "../../Routes/roleAccess";

const HelpMenuDropdown = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [awaitingCount, setAwaitingCount] = useState(0);

  const canManageSupport = useMemo(() => {
    const roles = getAuthRoleNames();

    return roles.some((role) => ADMIN_ROLES.includes(role));
  }, []);

  const canUseSupportChat = useMemo(() => {
    const roles = getAuthRoleNames();

    return roles.some((role) => STUDIO_WEB_ROLES.includes(role));
  }, []);

  const canUseWhatsappExtension = useMemo(() => {
    const roles = getAuthRoleNames();

    return roles.some((role) => ["owner", "manager", "receptionist"].includes(role));
  }, []);

  const helpCenterUrl = useMemo(() => getHelpCenterBaseUrl(), []);
  const extensionHelpUrl = useMemo(
    () => getHelpArticleUrl(HELP_ARTICLE_WHATSAPP_EXTENSION, i18n.language),
    [i18n.language]
  );

  const loadSummary = useCallback(async () => {
    if (!canManageSupport) {
      return;
    }

    try {
      const response = await getSupportTicketSummary();
      if (response.success) {
        setAwaitingCount(response.data?.awaiting_tenant || 0);
      }
    } catch {
      setAwaitingCount(0);
    }
  }, [canManageSupport]);

  useEffect(() => {
    void loadSummary();

    const onRefresh = () => {
      void loadSummary();
    };

    window.addEventListener(SUPPORT_SUMMARY_REFRESH_EVENT, onRefresh);
    window.addEventListener("focus", onRefresh);
    const intervalId = window.setInterval(onRefresh, 60_000);

    return () => {
      window.removeEventListener(SUPPORT_SUMMARY_REFRESH_EVENT, onRefresh);
      window.removeEventListener("focus", onRefresh);
      window.clearInterval(intervalId);
    };
  }, [loadSummary, ticketOpen, feedbackOpen]);

  return (
    <>
      <Dropdown
        isOpen={open}
        toggle={() => setOpen((current) => !current)}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle position-relative"
          aria-label={t("header.helpMenu.label")}
        >
          <i className="ri-settings-3-line fs-22" />
          {awaitingCount > 0 ? (
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-warning">
              {awaitingCount > 9 ? "9+" : awaitingCount}
            </span>
          ) : null}
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-end">
          <DropdownItem
            tag="a"
            href={helpCenterUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="ri-book-open-line me-2 align-middle" />
            {t("header.helpMenu.center")}
          </DropdownItem>
          <DropdownItem tag={Link} to="/help">
            <i className="ri-guide-line me-2 align-middle" />
            {t("header.helpMenu.guides")}
          </DropdownItem>
          <DropdownItem
            tag="a"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="ri-google-play-line me-2 align-middle" />
            {t("header.helpMenu.playStore")}
          </DropdownItem>
          {canUseWhatsappExtension ? (
            <>
              {canManageSupport ? (
                <DropdownItem tag={Link} to="/settings/whatsapp-extension" onClick={() => setOpen(false)}>
                  <i className="ri-whatsapp-line me-2 align-middle" />
                  {t("header.helpMenu.whatsappExtension")}
                </DropdownItem>
              ) : (
                <DropdownItem
                  tag="a"
                  href={extensionHelpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="ri-whatsapp-line me-2 align-middle" />
                  {t("header.helpMenu.whatsappExtension")}
                </DropdownItem>
              )}
            </>
          ) : null}
          {canUseSupportChat || canManageSupport ? (
            <>
              <div className="dropdown-divider" />
              {canUseSupportChat ? (
                <DropdownItem tag={Link} to="/support/chat" onClick={() => setOpen(false)}>
                  <i className="ri-chat-3-line me-2 align-middle" />
                  {t("header.helpMenu.supportChat")}
                </DropdownItem>
              ) : null}
              {canManageSupport ? (
                <>
                  <DropdownItem
                    onClick={() => {
                      setOpen(false);
                      setTicketOpen(true);
                    }}
                  >
                    <i className="ri-customer-service-2-line me-2 align-middle" />
                    {t("header.helpMenu.openTicket")}
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      setOpen(false);
                      setFeedbackOpen(true);
                    }}
                  >
                    <i className="ri-chat-smile-2-line me-2 align-middle" />
                    {t("header.helpMenu.sendFeedback")}
                  </DropdownItem>
                  <DropdownItem
                    tag={Link}
                    to={
                      awaitingCount > 0
                        ? "/support/tickets?status=awaiting_tenant"
                        : "/support/tickets"
                    }
                    className="d-flex align-items-center justify-content-between"
                    onClick={() => dispatchSupportSummaryRefresh()}
                  >
                    <span>
                      <i className="ri-file-list-3-line me-2 align-middle" />
                      {t("header.helpMenu.myTickets")}
                    </span>
                    {awaitingCount > 0 ? (
                      <Badge color="warning" pill>
                        {awaitingCount}
                      </Badge>
                    ) : null}
                  </DropdownItem>
                </>
              ) : null}
            </>
          ) : null}
        </DropdownMenu>
      </Dropdown>

      <SupportTicketModal
        isOpen={ticketOpen}
        toggle={() => setTicketOpen((current) => !current)}
        mode="ticket"
        onCreated={() => dispatchSupportSummaryRefresh()}
      />
      <SupportTicketModal
        isOpen={feedbackOpen}
        toggle={() => setFeedbackOpen((current) => !current)}
        mode="feedback"
        onCreated={() => dispatchSupportSummaryRefresh()}
      />
    </>
  );
};

export default HelpMenuDropdown;
